import { useState, useEffect } from 'react'
import PageContainer from '../components/layout/PageContainer'
import PageHeader from '../components/layout/PageHeader'
import { useAuthStore } from '../store/authStore'
import { doc, updateDoc } from 'firebase/firestore'
import { db, auth } from '../config/firebase'
import { updateProfile } from 'firebase/auth'
import toast from 'react-hot-toast'
import { User, MapPin, CreditCard, Camera, CheckCircle } from 'lucide-react'
import { User as UserType } from '../types'

export const UserProfilePage = ({ isEmbedded = false }: { isEmbedded?: boolean }) => {
    const { user, userData: initialUserData, setUserData, setUser } = useAuthStore() // Added setUser
    const [loading, setLoading] = useState(false)

    // Local state for the form
    const [formData, setFormData] = useState<Partial<UserType> & { displayName: string }>({ // Added displayName
        displayName: '',
        cpf: '',
        identificationCode: '',
        phone: '',
        birthDate: '',
        address: {
            zipCode: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: ''
        }
    })

    useEffect(() => {
        if (initialUserData || user) {
            setFormData({
                displayName: user?.displayName || (initialUserData as any)?.name || '', // Load name
                cpf: initialUserData?.cpf || '',
                identificationCode: initialUserData?.identificationCode || '',
                phone: initialUserData?.phone || '',
                birthDate: initialUserData?.birthDate || '',
                address: {
                    zipCode: initialUserData?.address?.zipCode || '',
                    street: initialUserData?.address?.street || '',
                    number: initialUserData?.address?.number || '',
                    complement: initialUserData?.address?.complement || '',
                    neighborhood: initialUserData?.address?.neighborhood || '',
                    city: initialUserData?.address?.city || '',
                    state: initialUserData?.address?.state || ''
                }
            })
        }
    }, [initialUserData, user])

    const handleMask = (field: string, value: string) => {
        let cleanValue = value.replace(/\D/g, '')

        if (field === 'cpf') {
            cleanValue = cleanValue.substring(0, 11)
            return cleanValue
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d{1,2})/, '$1-$2')
                .replace(/(-\d{2})\d+?$/, '$1')
        }

        if (field === 'phone') {
            cleanValue = cleanValue.substring(0, 11)
            return cleanValue
                .replace(/(\d{2})(\d)/, '($1) $2')
                .replace(/(\d{5})(\d)/, '$1-$2')
                .replace(/(-\d{4})\d+?$/, '$1')
        }

        if (field === 'address.zipCode') {
            cleanValue = cleanValue.substring(0, 8)
            return cleanValue.replace(/(\d{5})(\d)/, '$1-$2')
        }

        return value
    }

    const handleChange = (field: string, value: string) => {
        const maskedValue = ['cpf', 'phone', 'address.zipCode'].includes(field)
            ? handleMask(field, value)
            : value

        setFormData(prev => {
            if (field.includes('.')) {
                const [parent, child] = field.split('.')
                return {
                    ...prev,
                    [parent]: {
                        // @ts-ignore
                        ...prev[parent],
                        [child]: maskedValue
                    }
                }
            }
            return { ...prev, [field]: maskedValue }
        })
    }

    const handleSave = async () => {
        if (!user) return
        setLoading(true)

        try {
            // 1. Update Firestore
            const userRef = doc(db, 'users', user.uid)
            const { displayName, ...firestoreData } = formData // Separate displayName if it's not in UserType, but usually we save it too

            await updateDoc(userRef, {
                ...firestoreData,
                name: displayName, // Save as 'name' in Firestore to match User type usually
                displayName: displayName // Save as 'displayName' for consistency
            })

            // 2. Update Firebase Auth Profile
            if (auth.currentUser && displayName !== user.displayName) {
                await updateProfile(auth.currentUser, {
                    displayName: displayName
                })
                // Update local auth store user object
                setUser({ ...user, displayName: displayName })
            }

            // 3. Update local store userData
            const updatedData = { ...initialUserData, ...firestoreData, name: displayName, displayName: displayName }
            setUserData(updatedData as any)

            // Also update Auth User object if name changed
            if (user && displayName !== user.displayName) {
                setUser({ ...user, displayName: displayName })
            }

            toast.success('Perfil atualizado com sucesso!')
        } catch (error) {
            console.error(error)
            toast.error('Erro ao atualizar perfil.')
        } finally {
            setLoading(false)
        }
    }

    // ... content ...

    const content = (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header Card */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#001c28]/5 flex flex-col md:flex-row items-center gap-8">
                <div className="relative group">
                    <div className="w-32 h-32 rounded-full overflow-hidden bg-[#001c28] border-4 border-[#d4a94d] shadow-xl">
                        {user?.photoURL ? (
                            <img src={user.photoURL} alt="Avatar" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-4xl font-black text-[#d4a94d] uppercase">
                                {user?.displayName?.[0] || 'U'}
                            </div>
                        )}
                    </div>
                    <button className="absolute bottom-0 right-0 p-2 bg-[#d4a94d] text-[#001c28] rounded-full shadow-lg hover:bg-[#bfa75d] transition-colors">
                        <Camera size={20} />
                    </button>
                </div>

                <div className="text-center md:text-left flex-1">
                    <h2 className="text-3xl font-black text-[#001c28] uppercase italic tracking-tighter mb-1">{user?.displayName}</h2>
                    <p className="text-[#001c28]/60 font-medium mb-4">{user?.email}</p>
                    <div className="inline-flex items-center px-3 py-1 bg-[#001c28]/5 rounded-lg border border-[#001c28]/10">
                        <CreditCard size={14} className="text-[#d4a94d] mr-2" />
                        <span className="text-[#001c28] font-bold text-xs uppercase tracking-wide">
                            {(initialUserData as any)?.role === 'admin' ? 'Administrador' : (initialUserData as any)?.role === 'owner' ? 'Gestor' : 'Membro'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Form Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Dados Pessoais */}
                <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#001c28]/5 space-y-6">
                    <div className="flex items-center gap-2 mb-2">
                        <User className="text-[#d4a94d]" size={20} />
                        <h3 className="text-lg font-black text-[#001c28] uppercase tracking-wide">Dados Pessoais</h3>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Nome Completo</label>
                            <input
                                type="text"
                                value={formData.displayName}
                                onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                                placeholder="Seu nome completo"
                                className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d] placeholder:text-[#001c28]/20"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">CPF</label>
                            <input
                                type="text"
                                value={formData.cpf}
                                onChange={(e) => handleChange('cpf', e.target.value)}
                                placeholder="000.000.000-00"
                                maxLength={14}
                                className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d] placeholder:text-[#001c28]/20"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Data de Nascimento</label>
                            <input
                                type="date"
                                value={formData.birthDate}
                                onChange={(e) => handleChange('birthDate', e.target.value)}
                                className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d]"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Matrícula / ID Funcional</label>
                            <input
                                type="text"
                                value={formData.identificationCode}
                                onChange={(e) => handleChange('identificationCode', e.target.value)}
                                placeholder="Ex: 123456"
                                className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d] placeholder:text-[#001c28]/20"
                            />
                        </div>
                    </div>
                </div>

                {/* Contato e Endereço */}
                <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#001c28]/5 space-y-6">
                    <div className="flex items-center gap-2 mb-2">
                        <MapPin className="text-[#d4a94d]" size={20} />
                        <h3 className="text-lg font-black text-[#001c28] uppercase tracking-wide">Endereço e Contato</h3>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Telefone / WhatsApp</label>
                            <input
                                type="text"
                                value={formData.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                                placeholder="(00) 00000-0000"
                                maxLength={15}
                                className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d] placeholder:text-[#001c28]/20"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">CEP</label>
                                <input
                                    type="text"
                                    value={formData.address?.zipCode}
                                    onChange={(e) => handleChange('address.zipCode', e.target.value)}
                                    placeholder="00000-000"
                                    maxLength={9}
                                    className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d] placeholder:text-[#001c28]/20"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Estado</label>
                                <input
                                    type="text"
                                    value={formData.address?.state}
                                    onChange={(e) => handleChange('address.state', e.target.value)}
                                    placeholder="UF"
                                    maxLength={2}
                                    className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d] placeholder:text-[#001c28]/20"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Cidade</label>
                            <input
                                type="text"
                                value={formData.address?.city}
                                onChange={(e) => handleChange('address.city', e.target.value)}
                                placeholder="Cidade"
                                className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d] placeholder:text-[#001c28]/20"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Rua, Número e Bairro</label>
                            <input
                                type="text"
                                value={formData.address?.street}
                                onChange={(e) => handleChange('address.street', e.target.value)}
                                placeholder="Endereço completo"
                                className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d] placeholder:text-[#001c28]/20"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end pt-4">
                <button
                    onClick={handleSave}
                    disabled={loading}
                    className="bg-[#001c28] text-white px-8 py-4 rounded-xl font-black uppercase tracking-widest hover:bg-[#001c28]/90 transition-all shadow-lg shadow-[#001c28]/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading ? (
                        'Salvando...'
                    ) : (
                        <>
                            <CheckCircle size={20} className="text-[#d4a94d]" />
                            Salvar Alterações
                        </>
                    )}
                </button>
            </div>
        </div>
    )

    if (isEmbedded) return content

    return (
        <PageContainer>
            <PageHeader
                title="Meu Perfil"
                description="Gerencie seus dados pessoais e de contato."
            />
            {content}
        </PageContainer>
    )
}
